/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { Skeleton } from 'antd';
import { CSSProperties, useCallback } from 'react';
import { Suspense } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import styled from '@emotion/styled';
import { css } from '@emotion/css';
import { ResultMeta } from 'packages/omnitag/results/result-meta';
import { MinusSquareOutlined, PlusSquareOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import { ThumbnailImageAndLink } from 'packages/omnitag/results/thumbnail-image-and-link';
import { OmnitagResultItem } from 'packages/lib/api/omnitag/contract/omnitag-search';
import {
  toggleSelectedResultTransaction,
  selectedResultsAtom,
} from 'packages/omnitag/state/selected-results';
import { purple } from '@ant-design/colors';
import { customDefaultTheme } from 'packages/lib/common-ui/theme';
import { useRecoilValue } from 'recoil';
import { uiThemeAtom } from 'packages/at-shell/state/recoil/ui';

const MOTIONAL_PURPLE = customDefaultTheme.token?.colorPrimary ?? purple[5];

const styles: { [name: string]: CSSProperties } = {
  thumbnailImage: {
    width: '240px',
    height: '158px',
    objectFit: 'contain',
  },
};

export const thumbnailContainerClass = css`
  min-width: 240px;
  aspect-ratio: 240/158;
  background: rgba(190, 190, 190, 0.2);
  border-radius: 0.25rem;
  /* margin: 0 1rem 1rem 0; */
  position: relative;
  overflow: hidden;
  .ant-skeleton-element .ant-skeleton-image {
    width: 240px;
    aspect-ratio: 240/158;
    height: auto;
  }
`;

const BoundImage = css`
  max-height: 100vh;
  place-self: center;
`;
const ImageContainer = styled.div`
  cursor: pointer;
  container-type: inline-size;
  display: grid;
  height: 100%;
  :not(:root):fullscreen {
    grid-template-columns: 1fr 400px;
  }
  &:not(:fullscreen):hover .annotated-image {
    filter: url(#blurFilter);
  }
`;

const selectedClassDark = css`
  outline: 5px solid ${MOTIONAL_PURPLE};
`;
const selectedClassLight = css`
  outline: 5px solid ${'#9f9fffff'};
`;

const iconButtonTopRight = css`
  position: absolute;
  right: 0;
  top: 0;
  z-index: 10;
  font-size: 1.5rem;
  &:hover {
    opacity: 0.5;
  }
  &:active {
    opacity: 0.75;
  }
`;

const selectionIcon = css`
  display: none;
  color: #b3ecff;
  .image-container:hover & {
    display: block;
  }
  *:not(:root):fullscreen & {
    display: none;
  }
`;

export function ResultItem({
  result,
}: {
  result: OmnitagResultItem;
}): JSX.Element | null {
  const selectedSet = useAtomValue(selectedResultsAtom);
  const theme = useRecoilValue(uiThemeAtom);
  const isSelected =
    result && Array.from(selectedSet).some((r) => r.id === result.id);
  const toggleSelectedResult = useSetAtom(toggleSelectedResultTransaction);

  const toggleSelected = useCallback(
    (e: React.MouseEvent<HTMLSpanElement, MouseEvent>): void => {
      e.stopPropagation();

      toggleSelectedResult(result);
    },
    [result, toggleSelectedResult]
  );

  return (
    <div
      className={classNames(thumbnailContainerClass, {
        [selectedClassLight]: isSelected && theme === 'light',
        [selectedClassDark]: isSelected && theme === 'dark',
      })}
    >
      <div style={styles.thumbnailImage}>
        <Suspense fallback={<Skeleton.Image active />}>
          <ImageContainer className="image-container">
            {isSelected ? (
              <MinusSquareOutlined
                className={classNames(iconButtonTopRight, selectionIcon)}
                onClick={toggleSelected}
              />
            ) : (
              <PlusSquareOutlined
                className={classNames(iconButtonTopRight, selectionIcon)}
                onClick={toggleSelected}
              />
            )}
            <ThumbnailImageAndLink
              jobStatus={null}
              className={BoundImage}
              result={result}
            />
            <ResultMeta result={result} />
          </ImageContainer>
        </Suspense>
      </div>
    </div>
  );
}
